/** @file Main file of the application. */
import * as Sentry from '@sentry/vue';
import axios from 'axios';
import axiosRetry from 'axios-retry';
import * as detectBrowser from 'detect-browser';
import moment from 'moment';
import { Maybe, Nothing } from 'purify-ts/Maybe';
import Vue from 'vue';
import { datetime } from '@codegrade/utils';
// Styles MUST be imported BEFORE any component so that component CSS rules with
// the same specificity override the global style.
// eslint-disable-next-line no-restricted-imports
import 'bootstrap-vue/dist/bootstrap-vue.css';
import 'bootstrap/dist/css/bootstrap.css';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import './style/index.less';
import App from '@/App';
import { BREAKPOINTS } from '@/constants';
import * as utils from '@/utils';
import client, { unauthorizedClient } from './client';
import router from './router';
import * as store from './store';
import 'reflect-metadata';
import './icons';
import './register-components';
// Some users might want to block sentry which should be just fine.
if (SENTRY_DSN && Sentry) {
    try {
        Sentry.init({
            Vue,
            dsn: SENTRY_DSN,
            integrations: [new Sentry.BrowserTracing()],
            release: `CodeGra.de@${COMMIT_HASH}`,
            autoSessionTracking: false,
            tracesSampleRate: IS_PRODUCTION ? 0.05 : 0.5,
            attachProps: true,
            logErrors: true,
            beforeSend: (event, hint) => {
                // Ignore 404 errors.
                const exc = hint.originalException;
                if (exc?.response?.status === 404) {
                    return null;
                }
                return event;
            },
        });
    }
    catch (e) {
        // eslint-disable-next-line
        console.error(e);
    }
}
window.cgLogging = { doEventLogging: false, doMetricLogging: false };
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const NOT_LOGGED_IN_TOAST_TAG = 'NotLoggedIn';
Vue.config.productionTip = false;
Vue.directive('focus', {
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    inserted(el, other) {
        const value = other.value;
        if (value != null && typeof value !== 'boolean') {
            // eslint-disable-next-line no-console
            console.warn('Wrong type passed to focus directive', value);
        }
        if (value !== false) {
            el.focus();
        }
    },
});
moment.relativeTimeThreshold('h', 48);
moment.defineLocale('en-original', {
    parentLocale: 'en',
});
moment.updateLocale('en', {
    relativeTime: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        past(input) {
            return input === 'just now' ? input : `${input} ago`;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        future(input) {
            return input === 'just now' ? input : `in ${input}`;
        },
        s: 'just now',
        ss: '%d seconds',
        m: 'a minute',
        mm: '%d minutes',
        h: 'an hour',
        hh: '%d hours',
        d: 'a day',
        dd: '%d days',
        M: 'a month',
        MM: '%d months',
        y: 'a year',
        yy: '%d years',
    },
    calendar: {
        lastDay: '[yesterday at] LT',
        sameDay: '[today at] LT',
        nextDay: '[tomorrow at] LT',
        lastWeek: '[last] dddd [at] LT',
        nextWeek: 'dddd [at] LT',
        sameElse: 'YYYY-MM-DD HH:mm',
    },
});
axios.interceptors.request.use(config => {
    const { url } = config;
    store.UserStore.dangerousSession().ifJust(({ token }) => {
        if (url != null && utils.urlSameOrigin(url) && !config.headers.Authorization) {
            config.headers.Authorization = `Bearer ${token}`;
        }
    });
    return config;
});
// Fix axios automatically parsing all responses as JSON... WTF!!!
axios.defaults.transformResponse = [
    function defaultTransformResponse(data, headers) {
        const contentType = headers['content-type'];
        if (contentType != null && contentType.startsWith('application/json')) {
            // Somehow axios gives us an ArrayBuffer sometimes, even though
            // the Content-Type header is application/json. JSON.parse does
            // not work on ArrayBuffers (they're silently converted to the
            // string "[object ArrayBuffer]", which is invalid JSON), so we
            // must do that ourselves.
            if (data instanceof ArrayBuffer) {
                const view = new Int8Array(data);
                const dataStr = String.fromCharCode.apply(null, view);
                return JSON.parse(dataStr);
            }
            return JSON.parse(data);
        }
        else {
            return data;
        }
    },
];
axiosRetry(axios, {
    retries: 3,
    retryDelay: (retryNumber = 0) => {
        const delay = 2 ** retryNumber * 500;
        const randomSum = delay * 0.2 * Math.random(); // 0-20% of the delay
        return delay + randomSum;
    },
});
Vue.prototype.$http = axios;
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$inLTI', false, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$ltiProvider', null, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$LTIAssignmentId', null, null, true);
try {
    Vue.prototype.$devMode = process.env.NODE_ENV === 'development';
}
catch (e) {
    Vue.prototype.$devMode = false;
}
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
Vue.prototype.$routeParamAsId = function $routeParamAsId(name) {
    const res = utils.parseOrKeepFloat(this.$route.params[name]);
    return Number.isNaN(res) ? undefined : res;
};
Vue.prototype.$afterRerender = utils.doubleRequestAnimationFrame;
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
Vue.prototype.$waitForRef = async function waitForRef(refName, retries = 5) {
    for (let i = 0; i < retries; i++) {
        if (this.$refs[refName] == null) {
            // eslint-disable-next-line no-await-in-loop
            await this.$afterRerender();
        }
        else {
            break;
        }
    }
    return this.$refs[refName];
};
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
Vue.prototype.$withRef = async function withRef(refName, f, retries = 5) {
    const ref = await this.$waitForRef(refName, retries);
    if (ref != null) {
        return Maybe.of(f(ref));
    }
    return Maybe.empty();
};
/** @returns The Root Instance typed with root specific properties. */
Vue.prototype.$getRootInstance = function $getRootInstance() {
    return this.$root;
};
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
Vue.prototype.$addToast = function $addToast(toast) {
    this.$root.$emit('cg::app::toast', toast);
};
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
Vue.prototype.$deleteToast = function $addToast(toast) {
    this.$root.$emit('cg::app::delete-toast', toast);
};
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
Vue.prototype.$updateNow = function updateNow() {
    this.$root.$emit('cg::root::update-now');
};
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$windowWidth', window.innerWidth, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$windowHeight', window.innerHeight, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$isSmallWindow', false, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$isMediumWindow', false, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$isLargeWindow', false, null, true);
// @ts-expect-error: Vue.util is not defined
Vue.util.defineReactive(Vue.prototype, '$isXLargeWindow', false, null, true);
/** Our customised Vue instance. */
export const MyVue = Vue.extend({
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        const now = datetime.momentUtc(Maybe.empty());
        return {
            // `Now` and `epoch` both contain the current time. They are
            // the same, except that `epoch` is recalculated every second
            // while `now` is set only every minute. We do not want `now`
            // to be updated as often because that would trigger a redraw
            // of the sidebar every second.
            now,
            epoch: now,
            caughtErrors: [],
            $loadFullNotifications: false,
        };
    },
    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    created() {
        axios.interceptors.response.use(res => res, (err) => {
            throw this.handleHttpError(err.response?.data, err.response?.status, err.config?.method, err.config?.url, err.config?.headers?.Authorization).mapOrDefault(msg => new Error(msg), err);
        });
        store.onVueCreated(this);
        client.onVueCreated(this);
        this.updateWindowSizes();
        window.addEventListener('resize', () => {
            this.updateWindowSizes();
        });
        this.$on('cg::root::update-now', () => this.updateTimes());
        // We use less than a minute for now and less than a second for epoch so
        // that they can be used to nicely count minutes and seconds elapsed
        // respectively. The exact amount less is arbitrary.
        setInterval(() => {
            this.now = this.getCurrentTime();
        }, 59000);
        setInterval(() => {
            this.epoch = this.getCurrentTime();
        }, 750);
        this._scheduleCheckForUpdates();
    },
    computed: {
        /** @returns A dict with information about the browser. */
        _browser() {
            return detectBrowser.detect();
        },
        /** @returns Whether the app is running in a Safari browser. */
        isSafari() {
            return this._browser?.name === 'safari';
        },
        /** @returns Whether the app is running in a Chrome, Chromium, or Edge browser. */
        isChrome() {
            const chromiums = ['chrome', 'edge-chromium'];
            return chromiums.includes(this._browser?.name);
        },
        /** @returns Whether the browser is running on Linux. */
        isLinux() {
            return this._browser?.os?.toLowerCase() === 'linux';
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        $now() {
            return this.now;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        $epoch() {
            return this.epoch;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        serverTimeOffset() {
            return store.SiteSettingsStore.getServerTimeOffset();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        getCurrentTime() {
            return store.SiteSettingsStore.currentTimeGetter();
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        loggedIn() {
            return store.UserStore.loggedIn();
        },
    },
    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        updateWindowSizes() {
            const { innerWidth, innerHeight } = window;
            // Ignore the typescript errors below because the props are declared as readonly
            // to prevent them from being updated in other components.
            // @ts-expect-error: This is declared as readonly.
            this.$windowHeight = innerHeight;
            // @ts-expect-error: This is declared as readonly.
            this.$windowWidth = innerWidth;
            // @ts-expect-error: This is declared as readonly.
            this.$isSmallWindow = innerWidth <= BREAKPOINTS.smallWidth;
            // @ts-expect-error: This is declared as readonly.
            this.$isMediumWindow = innerWidth >= BREAKPOINTS.mediumWidth;
            // @ts-expect-error: This is declared as readonly.
            this.$isLargeWindow = innerWidth >= BREAKPOINTS.largeWidth;
            // @ts-expect-error: This is declared as readonly.
            this.$isXLargeWindow = innerWidth >= BREAKPOINTS.xlargeWidth;
            document.documentElement.style.setProperty('--vh', `${innerHeight / 100}px`);
        },
        /**
         * Get the current time with a high enough precision to do relative calculations compared to
         * a passed `base`.
         *
         * @param base The base to which you want to compare now, if this is close to `$now` we will
         * return `$epoch` for a higher precision.
         * @returns A moment that represents now with a good enough precision.
         */
        $getNow(base) {
            if (!base.isValid() || datetime.diff(moment.utc(), base).abs().asMinutes() > 2) {
                return this.$now;
            }
            return this.$epoch;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        updateTimes() {
            const now = this.getCurrentTime();
            this.epoch = now;
            this.now = now;
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        notLoggedInError(message) {
            this.$addToast({
                tag: NOT_LOGGED_IN_TOAST_TAG,
                title: 'Not logged in',
                message,
                variant: 'danger',
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        connectionError() {
            this.$addToast({
                tag: 'ConnectionError',
                title: 'Connection error',
                message: 'There was an error connecting to the server... Please try again later.',
                variant: 'danger',
            });
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        _checkForUpdates() {
            if (!IS_PRODUCTION) {
                return;
            }
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            const buildUrl = (path) => utils.buildUrl(path, { query: { cacheBuster: Math.random() } });
            const responses = Object.freeze({
                200: (response) => response.text(),
            });
            unauthorizedClient
                .rawRequest({
                method: 'GET',
                path: buildUrl(['static', 'commitHash']),
                responses,
            })
                .then(remoteCommit => {
                if (COMMIT_HASH !== remoteCommit) {
                    this.$emit('cg::app::toast', {
                        tag: 'UpdateAvailable',
                        title: 'CodeGrade update available!',
                        message: 'An updated version of CodeGrade is available. Please click here to reload the page and start using the latest version!',
                        href: '#',
                        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
                        onClick() {
                            window.location.reload();
                        },
                    });
                }
                else {
                    this._scheduleCheckForUpdates();
                }
            }, () => this._scheduleCheckForUpdates());
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        _scheduleCheckForUpdates() {
            setTimeout(() => {
                this._checkForUpdates();
            }, 10 * 60 * 1000);
        },
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        handleHttpError(err, status, method, url, authHeader) {
            switch (status) {
                case 401: {
                    if (method == null ||
                        url == null ||
                        method.toLowerCase() !== 'get' ||
                        url.startsWith('/api/v1/login') ||
                        router.currentRoute?.meta?.loginRequired !== true) {
                        return Nothing;
                    }
                    const storeToken = store.UserStore.dangerousSession().extract()?.token;
                    if (storeToken == null) {
                        this.notLoggedInError('You are currently not logged in. Please log in to view this page.');
                    }
                    else if (storeToken !== authHeader?.replace(/^Bearer\s*/, '')) {
                        return Nothing;
                    }
                    else {
                        this.notLoggedInError('Your session has expired. Please log in again to view this page.');
                    }
                    if (router.currentRoute.name !== 'login') {
                        this.$router.logoutAndRedirectAfterLogin();
                    }
                    return Nothing;
                }
                case 504: {
                    const msg = err?.data?.message;
                    if (msg == null) {
                        throw new Error('The server is very busy right now. Please try again in a moment.');
                    }
                    else {
                        return Nothing;
                    }
                }
                case undefined:
                case null: {
                    this.connectionError();
                    return Nothing;
                }
                default: {
                    return Nothing;
                }
            }
        },
    },
    watch: {
        isSafari: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            handler() {
                if (this.isSafari) {
                    document.body.classList.add('is-safari');
                }
                else {
                    document.body.classList.remove('is-safari');
                }
            },
        },
        loggedIn: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            handler(loggedIn) {
                if (loggedIn) {
                    this.$deleteToast(NOT_LOGGED_IN_TOAST_TAG);
                }
            },
        },
        serverTimeOffset: {
            immediate: true,
            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            handler() {
                this.updateTimes();
            },
        },
    },
});
// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export const makeApp = () => new MyVue({
    el: '#app',
    router,
    store: store.store,
    /** @returns The main element of CodeGrade. */
    render() {
        return this.$createElement(App);
    },
});
